import { createContext } from 'preact';
import { Dispatch, StateUpdater, useEffect, useMemo, useRef } from 'preact/hooks';
import { useContext } from 'preact/hooks';
import { ComponentChildren } from 'preact';
import type { CTAState } from 'server/types/cal-page';
import { MenuPane, MenuProps } from './types';
import { MenuChoice, MenuHR, MenuHeader, SettingsMenu } from './core';
import { Button } from '@components/buttons';
import { IcoDotsHorizontal, IcoX } from '@components/icons';
import { autoColor } from 'shared/colors';
import { createPortal } from 'preact/compat';
import { ColorOptions } from './color-options';

export type CTARadius = 'none' | 'small' | 'medium' | 'large' | 'full';

export type CtaPickerContextValue = {
  showPicker?: MenuPane<CTAState>;
  defaultUrl?: { url: string; description: ComponentChildren };
  renderLinkUrl?: (state: CTAState) => ComponentChildren;
};

const CtaPickerContext = createContext<CtaPickerContextValue>({});

export function useCtaPickerContext() {
  return useContext(CtaPickerContext);
}

export const CtaPickerProvider = CtaPickerContext.Provider;

function CTAPane(props: MenuProps<CTAState>) {
  const roundingChoices: CTARadius[] = ['none', 'small', 'medium', 'large', 'full'];
  const ctaPickerContext = useCtaPickerContext();
  const urlUI = ctaPickerContext.renderLinkUrl?.(props.state);
  return (
    <>
      <MenuHeader label="Call to Action" close={props.close} isBase />
      <div class="flex flex-col gap-2">
        <section class="flex flex-col p-1 px-2 gap-2 text-sm transition-all font-medium">
          <span>Button text</span>
          <input
            type="text"
            class="ruz-input"
            name="text"
            value={props.state.text}
            onInput={(e: any) => props.setState((s) => ({ ...s, text: e.target.value }))}
          />
        </section>
        <section class="flex flex-col p-1 px-2 gap-2 text-sm transition-all font-medium">
          <span>Link URL</span>
          <div class="flex gap-2">
            {urlUI && (
              <>
                {urlUI}
                <Button
                  title="Change"
                  onClick={() => props.setState((s) => ({ ...s, url: '', metadata: undefined }))}
                >
                  <IcoX />
                </Button>
              </>
            )}
            {!urlUI && (
              <>
                <input
                  type="url"
                  class="ruz-input"
                  name="url"
                  value={props.state.url}
                  onInput={(e: any) => props.setState((s) => ({ ...s, url: e.target.value }))}
                  placeholder="https://example.com"
                />
                {ctaPickerContext.showPicker && (
                  <Button
                    class="inline-flex items-center justify-center w-8 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onClick={() => {
                      ctaPickerContext.showPicker && props.pushPane(ctaPickerContext.showPicker);
                    }}
                    title="Browse for URL"
                  >
                    <IcoDotsHorizontal class="size-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </section>
        <MenuHR />
        <ColorOptions
          label="Background"
          name="bgcolor"
          value={props.state.bgcolor}
          onChange={(bgcolor) =>
            props.setState((s) => ({ ...s, bgcolor, fgcolor: autoColor(bgcolor) }))
          }
        />
        <ColorOptions
          label="Text"
          name="fgcolor"
          value={props.state.fgcolor}
          onChange={(fgcolor) => props.setState((s) => ({ ...s, fgcolor }))}
        />
        <MenuHR />
        <MenuChoice
          label="Rounding"
          choices={roundingChoices}
          name="borderRadius"
          value={props.state.borderRadius}
          onChange={(borderRadius) => props.setState((s) => ({ ...s, borderRadius }))}
          choiceUi={(option) => {
            switch (option) {
              case 'small':
                return 'SM';
              case 'medium':
                return 'MD';
              case 'large':
                return 'LG';
              case 'full':
                return 'XL';
              default:
                return option;
            }
          }}
        />
      </div>
    </>
  );
}

export function CTASettingsMenu(opts: {
  state: CTAState;
  setState: Dispatch<StateUpdater<CTAState>>;
  close(): void;
  onDelete(): void;
}) {
  // Used to attach the menu to an outer element so it can stack above
  // other elements while visible.
  const positionRef = useRef<HTMLElement>();
  const flyoutContainerRef = useMemo(() => document.createElement('div'), []);

  const reposition = () => {
    const el = positionRef.current;
    if (!el) {
      return;
    }
    const bounds = el.getBoundingClientRect();
    flyoutContainerRef.style.top = `${bounds.top}px`;
    flyoutContainerRef.style.left = `${bounds.left}px`;
  };

  useEffect(() => {
    document.body.append(flyoutContainerRef);
    flyoutContainerRef.className = 'fixed z-max';
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('scroll', reposition);
      flyoutContainerRef.remove();
    };
  }, []);

  return (
    <div
      class="relative"
      ref={(el) => {
        if (el && el !== positionRef.current) {
          positionRef.current = el;
          reposition();
        }
      }}
    >
      {createPortal(
        <SettingsMenu {...opts} position="top" basePane={CTAPane} />,
        flyoutContainerRef,
      )}
    </div>
  );
}
